import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CreditCard, Wallet, Globe, AlertTriangle, CheckCircle2, Save, RotateCcw } from "lucide-react";

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block font-label text-[11px] uppercase tracking-[0.22em] text-cream/70 mb-1.5">
      {label}
    </label>
    {children}
    {hint && <p className="mt-1.5 text-xs text-cream/50">{hint}</p>}
  </div>
);

const KeyStatus = ({ ok, missing_label }) =>
  ok ? (
    <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-label uppercase tracking-[0.2em]">
      <CheckCircle2 size={12} /> Keys configured
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-amber-400 text-xs font-label uppercase tracking-[0.2em]">
      <AlertTriangle size={12} /> {missing_label}
    </span>
  );

const MethodCard = ({
  icon: Icon,
  title,
  badge,
  enabled,
  onToggle,
  keys_ok,
  missing_msg,
  children,
  testid,
}) => (
  <section
    className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
    data-testid={testid}
  >
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-rose/30 to-gold/20 flex items-center justify-center flex-shrink-0">
          <Icon size={20} className="text-cream" />
        </div>
        <div>
          <h2 className="font-hero text-xl text-cream">{title}</h2>
          <p className="text-xs text-cream/60 mt-0.5">{badge}</p>
          {keys_ok !== undefined && (
            <div className="mt-2">
              <KeyStatus ok={keys_ok} missing_label={missing_msg} />
            </div>
          )}
        </div>
      </div>
      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        data-testid={`${testid}-toggle`}
      />
    </div>
    {enabled && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-white/10">
        {children}
      </div>
    )}
  </section>
);

const AdminPayments = () => {
  const [s, setS] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/payment/settings");
      setS(data);
      setOriginal(data);
    } catch {
      toast.error("Couldn't load payment settings");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const dirty = s && original && JSON.stringify(s) !== JSON.stringify(original);

  const setField = (k, v) => setS((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!s) return;
    // Sanity: at least one method must remain on
    if (!s.cod_enabled && !s.razorpay_enabled && !s.stripe_enabled) {
      toast.error("At least one payment method must remain enabled.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        cod_enabled: s.cod_enabled,
        razorpay_enabled: s.razorpay_enabled,
        stripe_enabled: s.stripe_enabled,
        cod_label: s.cod_label,
        cod_description: s.cod_description,
        cod_min_order: Number(s.cod_min_order || 0),
        cod_max_order: Number(s.cod_max_order || 0),
        razorpay_label: s.razorpay_label,
        razorpay_description: s.razorpay_description,
        stripe_label: s.stripe_label,
        stripe_description: s.stripe_description,
      };
      const { data } = await api.patch("/admin/payment/settings", body);
      setS(data);
      setOriginal(data);
      toast.success("Payment settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setS(original);

  if (!s) {
    return <div className="text-cream/60 p-6">Loading payment settings…</div>;
  }

  const enabledCount = [s.cod_enabled, s.razorpay_enabled, s.stripe_enabled].filter(Boolean).length;

  return (
    <div data-testid="admin-payments-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-label text-[11px] uppercase tracking-[0.24em] text-cream/60 mb-1">
            Commerce
          </p>
          <h1 className="font-hero text-3xl text-cream">Payment methods</h1>
          <p className="text-sm text-cream/60 mt-1">
            Toggle providers on/off and customize their checkout labels.
            Disabled methods are hidden from customers and rejected at the API layer.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`px-3 py-1.5 rounded-full font-label text-[11px] uppercase tracking-[0.2em] border ${
              enabledCount === 0
                ? "bg-rose/20 text-rose-100 border-rose/40"
                : "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
            }`}
            data-testid="payments-active-count"
          >
            {enabledCount} active
          </span>
          {dirty && (
            <button
              onClick={reset}
              className="px-4 h-10 rounded-xl border border-white/15 text-cream/80 hover:bg-white/5 inline-flex items-center gap-2 text-sm"
              data-testid="payments-reset-button"
            >
              <RotateCcw size={14} /> Discard
            </button>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="px-5 h-10 rounded-xl bg-gradient-to-br from-rose to-rose/80 text-cream hover:opacity-95 inline-flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="payments-save-button"
          >
            <Save size={14} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {/* COD */}
      <MethodCard
        icon={Wallet}
        title="Cash on delivery (COD)"
        badge="Pay-on-delivery for India · settles after Xpressbees delivery"
        enabled={!!s.cod_enabled}
        onToggle={(v) => setField("cod_enabled", v)}
        testid="payment-cod"
      >
        <Field label="Checkout label">
          <Input
            value={s.cod_label || ""}
            onChange={(e) => setField("cod_label", e.target.value)}
            className="bg-white/5 border-white/15 text-cream placeholder:text-cream/40"
            data-testid="payment-cod-label"
          />
        </Field>
        <Field label="Description">
          <Input
            value={s.cod_description || ""}
            onChange={(e) => setField("cod_description", e.target.value)}
            className="bg-white/5 border-white/15 text-cream placeholder:text-cream/40"
            data-testid="payment-cod-description"
          />
        </Field>
        <Field label="Minimum order (INR)" hint="0 = no minimum">
          <Input
            type="number"
            min="0"
            value={s.cod_min_order ?? 0}
            onChange={(e) => setField("cod_min_order", e.target.value)}
            className="bg-white/5 border-white/15 text-cream placeholder:text-cream/40"
            data-testid="payment-cod-min"
          />
        </Field>
        <Field label="Maximum order (INR)" hint="0 = no maximum (block large COD orders to limit RTO risk)">
          <Input
            type="number"
            min="0"
            value={s.cod_max_order ?? 0}
            onChange={(e) => setField("cod_max_order", e.target.value)}
            className="bg-white/5 border-white/15 text-cream placeholder:text-cream/40"
            data-testid="payment-cod-max"
          />
        </Field>
      </MethodCard>

      {/* RAZORPAY */}
      <MethodCard
        icon={CreditCard}
        title="Razorpay"
        badge="UPI · Cards · Netbanking · Wallets — Indian customers"
        enabled={!!s.razorpay_enabled}
        onToggle={(v) => setField("razorpay_enabled", v)}
        keys_ok={!!s.razorpay_keys_configured}
        missing_msg="Set RAZORPAY_KEY_ID + RAZORPAY_KEY_SECRET in backend .env"
        testid="payment-razorpay"
      >
        <Field label="Checkout label">
          <Input
            value={s.razorpay_label || ""}
            onChange={(e) => setField("razorpay_label", e.target.value)}
            className="bg-white/5 border-white/15 text-cream placeholder:text-cream/40"
            data-testid="payment-razorpay-label"
          />
        </Field>
        <Field label="Description">
          <Input
            value={s.razorpay_description || ""}
            onChange={(e) => setField("razorpay_description", e.target.value)}
            className="bg-white/5 border-white/15 text-cream placeholder:text-cream/40"
            data-testid="payment-razorpay-description"
          />
        </Field>
      </MethodCard>

      {/* STRIPE */}
      <MethodCard
        icon={Globe}
        title="Stripe"
        badge="International cards in INR — for non-Indian customers"
        enabled={!!s.stripe_enabled}
        onToggle={(v) => setField("stripe_enabled", v)}
        keys_ok={!!s.stripe_keys_configured}
        missing_msg="Set STRIPE_SECRET_KEY in backend .env"
        testid="payment-stripe"
      >
        <Field label="Checkout label">
          <Input
            value={s.stripe_label || ""}
            onChange={(e) => setField("stripe_label", e.target.value)}
            className="bg-white/5 border-white/15 text-cream placeholder:text-cream/40"
            data-testid="payment-stripe-label"
          />
        </Field>
        <Field label="Description">
          <Input
            value={s.stripe_description || ""}
            onChange={(e) => setField("stripe_description", e.target.value)}
            className="bg-white/5 border-white/15 text-cream placeholder:text-cream/40"
            data-testid="payment-stripe-description"
          />
        </Field>
      </MethodCard>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <p className="font-label text-[11px] uppercase tracking-[0.24em] text-cream/60 mb-2">
          How this works
        </p>
        <ul className="text-sm text-cream/70 space-y-1.5 leading-relaxed list-disc pl-5">
          <li>
            Disabling a method <strong>hides</strong> its option on the customer Checkout page
            <em>and</em> blocks the API endpoint from accepting it (prevents bypass).
          </li>
          <li>
            A method must have its API keys set in the backend <code className="text-rose-200">.env</code> to actually
            process payments — the badge above tells you whether keys are configured.
          </li>
          <li>
            COD min/max guards reduce RTO (return-to-origin) risk for high-value orders.
          </li>
          <li>You cannot disable all three methods — at least one must stay on.</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminPayments;
